import React from 'react';

import { ExpandOutline } from '@lyearn/icons';
import VideoPlayer from '@lyearn/shared/components/Players';
import { cn } from '@lyearn/style';
import { Box, IconButton, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import MediaListContainer from '@/components/Media/components/MediaListContainer';
import { MediaListContainerProps } from '@/components/Media/components/MediaListContainer/types';
import RichTextViewer from '@/components/RichTextViewer';
import stopPropagation from '@/consts/stopPropogation';
import { MediaKindEnum } from '@/types/schema';

import { maxH298, optionCodeStyles } from './styles';
import {
  RenderOptionAttachmentsParams,
  RenderOptionKeyCodeIconParams,
  RenderOptionKeyCodeLabelParams,
  RenderOptionKeyCodeParams,
  RenderOptionStatusParams,
  RenderOptionTextParams,
} from './types';

const VIDEO_CLASSES = {
  video: maxH298,
};

export const renderKeyCodeIcon = ({ Icon, fillColorToken }: RenderOptionKeyCodeIconParams) => (
  <Icon className={'h-20 w-20'} fillColorToken={fillColorToken} />
);

export const renderKeyCodeLabel = ({ label, className }: RenderOptionKeyCodeLabelParams) => (
  <Typography className={className} variant="heading-xxs">
    {label}
  </Typography>
);

export const renderKeyCode = ({ className, children, style }: RenderOptionKeyCodeParams) => {
  return (
    <Stack
      alignItems="center"
      className={cn('h-24 w-24 flex-shrink-0 rounded-4', className)}
      justifyContent="center"
      style={style}>
      {children}
    </Stack>
  );
};

export const renderOptionText = ({
  optionText,
  className,
  style,
  truncate,
  truncateLength,
  secondaryBackgroundColor,
}: RenderOptionTextParams) => (
  <RichTextViewer
    className={cn(
      'flex-shrink ms-10',
      optionCodeStyles(className, secondaryBackgroundColor),
      className,
    )}
    containerClassName="flex-shrink"
    html={optionText}
    style={style}
    truncate={truncate}
    truncateLength={truncateLength}
    variant="body-short-01"
  />
);

export const renderAttachments = ({ attachments, className }: RenderOptionAttachmentsParams) => {
  if (!attachments?.length || !attachments[0].url) {
    return null;
  }

  const renderContent: MediaListContainerProps['renderList'] = ({ medias, onClick }) => {
    if (medias[0].kind === MediaKindEnum.Video) {
      return (
        <Box className={cn('relative cursor-pointer overflow-hidden rounded-8', className)}>
          <VideoPlayer classes={VIDEO_CLASSES} provider="html5" url={medias[0].url || ''} />
        </Box>
      );
    }

    const onClickIcon = (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (!event) return;
      stopPropagation(event);
      onClick(medias[0]);
    };

    return (
      <Stack
        className={cn(
          'group pointer-events-auto relative w-full cursor-pointer overflow-hidden rounded-8 border border-border',
          className,
        )}
        justifyContent="center">
        <Image
          useImg
          alt="question media"
          className={cn('h-auto object-contain xs:w-192', maxH298)}
          src={medias[0].url || ''}
        />
        <IconButton
          Icon={ExpandOutline}
          appearance="secondary"
          className="absolute bottom-8 opacity-0 end-8 group-hover:opacity-100"
          shape="square"
          onClick={onClickIcon}
        />
      </Stack>
    );
  };

  return <MediaListContainer medias={attachments} renderList={renderContent} />;
};

export const renderOptionStatus = ({
  className,
  labelKey,
  t,
  style,
  params,
}: RenderOptionStatusParams) => {
  if (!labelKey) {
    return null;
  }

  return (
    <Typography
      className={cn(
        className,
        // Fixed (not min-) width so every row's label starts at the same x-position —
        // otherwise shorter labels like "Correct" left extra room for the option text
        // to wrap into, while longer ones like "Partially correct · 75%" didn't.
        'w-[220px] flex-shrink-0 self-center ms-auto text-end',
        'xs:mt-4 xs:w-full xs:min-w-0 xs:basis-full xs:ms-0 xs:self-auto',
      )}
      style={style}
      variant="label-caps">
      {t(labelKey, params)}
    </Typography>
  );
};
